import { getData } from 'src/consumer/services/profileService'
import { getDataMapFromCommonResponse } from 'src/mentor/helpers/utilityFunctions'

export const PORTAL_VIEW_SETTINGS_URL =
  '/api/secure/practitioner/emr-settings/settings/portal-view-settings'

export const RTM_SIGNALLING_SETTING_KEY = 'use-rtm-signalling-on-mentor-portal'

let portalViewSettingsPromise = null

/**
 * Fetch the practitioner's portal-view settings once per session.
 *
 * Shared + cached on purpose: MentorLayout reads the patient-contact-details flag out of this same
 * response, and the app-shell signalling bridge reads the RTM flag, but they live in different
 * trees. One cached promise keeps that to a single request.
 *
 * Reads the data map regardless of the response code: when a practitioner has no settings document
 * the backend answers 4005 ("using default") and still carries the flags in the same `data` map.
 */
export function loadPortalViewSettings() {
  if (!portalViewSettingsPromise) {
    portalViewSettingsPromise = getData(PORTAL_VIEW_SETTINGS_URL)
      .then((resp) => (resp?.status === 200 ? getDataMapFromCommonResponse(resp) || {} : {}))
      .catch((error) => {
        // Don't cache a failure — a transient error would otherwise pin the portal to defaults
        // for the whole session.
        portalViewSettingsPromise = null
        throw error
      })
  }

  return portalViewSettingsPromise
}

export function resetPortalViewSettingsCache() {
  portalViewSettingsPromise = null
}

/**
 * Whether this practitioner's portal should run live Agora RTM signalling.
 *
 * Default is NO — the portal polls the check-in-request endpoints instead, which keeps the ~1.4MB
 * RTM SDK off the page. Anything unexpected (network error, missing key, old backend) resolves to
 * polling, because polling is the mode that works without any server support.
 */
export async function isRtmSignallingEnabledForPractitioner() {
  try {
    const settings = await loadPortalViewSettings()

    return settings?.[RTM_SIGNALLING_SETTING_KEY] === true
  } catch (error) {
    console.warn('[mentorSignalling] Could not resolve signalling mode; falling back to polling', error)

    return false
  }
}
